import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown, FiltersBar, IconButton, Page, SingleSelectFilter } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { PageLoader } from '~/routes/public/pages';
import WorkspaceMembersModal from '../WorkspaceMembersModal';
import ProjectMembersList from './ProjectMembersList';

export default function ProjectMembersTab({ project }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();

  const [isActive, setIsActive] = useState('true');
  const [members, setMembers] = useState(null);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [dialog, setDialog] = useState(null);

  // Read unfiltered and filter by status in the browser, so the add actions still see members the filter hides.
  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).members().get({ size: 1000 });
    setMembers(data);
  }, [api, workspace.id, project.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let isCancelled = false;

    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).projects(project.id).workspaceMembers({ size: 1000 });
        if (!isCancelled) setWorkspaceMembers(data);
      } catch {
        if (!isCancelled) setWorkspaceMembers([]);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [api, workspace.id, project.id]);

  const data = useMemo(() => {
    if (!members) return null;
    if (isActive === 'true') return members.filter((projectMember) => projectMember.isActuallyActive);
    if (isActive === 'false') return members.filter((projectMember) => !projectMember.isActuallyActive);
    return members;
  }, [members, isActive]);

  // The picker can reactivate an inactive row; Add All only inserts members with no row at all.
  const activelyAssignedMemberIds = useMemo(
    () =>
      new Set(
        (members ?? [])
          .filter((projectMember) => projectMember.isActive)
          .map((projectMember) => projectMember.member.id),
      ),
    [members],
  );
  const assignedMemberIds = useMemo(
    () => new Set((members ?? []).map((projectMember) => projectMember.member.id)),
    [members],
  );
  const hasRemaining = useMemo(
    () => workspaceMembers.some((member) => !activelyAssignedMemberIds.has(member.id)),
    [workspaceMembers, activelyAssignedMemberIds],
  );
  const hasRemainingActive = useMemo(
    () => workspaceMembers.some((member) => member.isActive && !assignedMemberIds.has(member.id)),
    [workspaceMembers, assignedMemberIds],
  );

  const addMembers = async (membersToAdd, failureMessage) => {
    if (membersToAdd.length === 0) return;

    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projectSettings(project.id)
        .members()
        .batch.add(membersToAdd.map((member) => ({ memberId: member.id })));

      const { succeeded } = data.summary;
      if (succeeded === data.rows.length) {
        toast.success('Members added successfully.');
      } else if (succeeded > 0) {
        toast.error(`Only ${succeeded} of the ${data.rows.length} selected members could be added.`);
      } else {
        toast.error(failureMessage);
      }
      await fetchData();
    } catch {
      toast.error(failureMessage);
    }
  };

  const handleAddMembers = () => {
    setDialog(
      <WorkspaceMembersModal
        projectId={project.id}
        projectMembers={(members ?? []).map((projectMember) => ({
          memberId: projectMember.member.id,
          isActive: projectMember.isActive,
        }))}
        onChange={(selectedMembers) => addMembers(selectedMembers, 'Failed to add the selected members.')}
        onClose={() => setDialog(null)}
      />,
    );
  };

  const handleAddAllMembers = async (employmentTypeId) => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .workspaceMembers({ size: 1000, isActive: 'true', employmentTypeId });
      await addMembers(
        data.filter((member) => !assignedMemberIds.has(member.id)),
        'Failed to add the workspace members.',
      );
    } catch {
      toast.error('Failed to add the workspace members.');
    }
  };

  if (!data) return <PageLoader />;

  const allAssignedTooltip = 'Every workspace member has been assigned to this project.';
  const allActiveAssignedTooltip = 'Every active workspace member has been assigned to this project.';

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Team</Page.Title>

        <Page.Actions>
          {project.permissions.edit && (
            <Dropdown align="right">
              {({ setIsOpen }) => (
                <>
                  <Dropdown.Trigger>
                    <IconButton icon="plus" tooltip="Add Members" onClick={() => setIsOpen(true)} />
                  </Dropdown.Trigger>

                  <Dropdown.Menu style={{ width: '17rem' }}>
                    <Dropdown.Item
                      data-testid="add_members_button"
                      disabled={!hasRemaining}
                      tooltip={hasRemaining ? undefined : allAssignedTooltip}
                      onClick={() => {
                        setIsOpen(false);
                        handleAddMembers();
                      }}>
                      Add Members
                    </Dropdown.Item>

                    <Dropdown.Item
                      data-testid="add_all_workspace_members_button"
                      disabled={!hasRemainingActive}
                      tooltip={hasRemainingActive ? undefined : allActiveAssignedTooltip}
                      onClick={() => {
                        setIsOpen(false);
                        handleAddAllMembers();
                      }}>
                      Add All Workspace Members
                    </Dropdown.Item>

                    <Dropdown.Item
                      data-testid="add_all_employee_members_button"
                      disabled={!hasRemainingActive}
                      tooltip={hasRemainingActive ? undefined : allActiveAssignedTooltip}
                      onClick={() => {
                        setIsOpen(false);
                        handleAddAllMembers('employee');
                      }}>
                      Add All Employee Members
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </>
              )}
            </Dropdown>
          )}
        </Page.Actions>
      </Page.TabHeader>

      <Page.Filters>
        <FiltersBar>
          <SingleSelectFilter
            name="isActive"
            icon="filter"
            placeholder="Member Status"
            options={[
              { id: 'true', name: 'Active' },
              { id: 'false', name: 'Inactive' },
            ]}
            value={isActive}
            onChange={({ target: { value } }) => setIsActive(value)}
          />
        </FiltersBar>
      </Page.Filters>

      <ProjectMembersList project={project} data={data} members={members} onRefetch={fetchData} />

      {dialog}
    </>
  );
}
